/**
 * Endpoints de **Gestión → Productos** (`/api/gestion/productos/*`).
 *
 * Las tres pestañas de la pantalla (combustibles, extras y combos) salen del
 * mismo listado: el filtro por tipo vive en la consulta, así que cada pestaña
 * tiene su propia clave de caché.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CategoriaProducto,
  DiferenciaDiariaRespuesta,
  DatosProducto,
  FiltrosProductos,
  MedicionesRespuesta,
  PrecioProgramado,
  ProductoFichaRespuesta,
  ProductosRespuesta,
} from './gestion.types';

const CLAVE = 'gestion';

export const productosKeys = {
  listado: (filtros: FiltrosProductos) => [CLAVE, 'productos', 'listado', filtros] as const,
  mediciones: (id: number | null, limite: number) =>
    [CLAVE, 'productos', 'mediciones', id, limite] as const,
  ficha: (id: number | null) => [CLAVE, 'productos', 'ficha', id] as const,
  categorias: () => [CLAVE, 'productos', 'categorias'] as const,
  programados: () => [CLAVE, 'productos', 'precios-programados'] as const,
  diferenciaDiaria: (mes: string) => [CLAVE, 'productos', 'diferencia-diaria', mes] as const,
};

/** Cadena de consulta de un objeto de filtros. */
function cadenaDe(filtros: object): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor !== undefined && valor !== null && valor !== '') {
      parametros.set(clave, String(valor));
    }
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const productosApi = {
  listado: (filtros: FiltrosProductos, signal?: AbortSignal): Promise<ProductosRespuesta> =>
    apiClient.get<ProductosRespuesta>(`/api/gestion/productos${cadenaDe(filtros)}`, { signal }),

  mediciones: (id: number, limite: number, signal?: AbortSignal): Promise<MedicionesRespuesta> =>
    apiClient.get<MedicionesRespuesta>(
      `/api/gestion/productos/${id}/mediciones${cadenaDe({ limite })}`,
      { signal },
    ),

  cambiarEstado: (id: number, estado: boolean): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>(`/api/gestion/productos/${id}/estado`, {
      body: { estado },
    }),

  diferenciaDiaria: (mes: string, signal?: AbortSignal): Promise<DiferenciaDiariaRespuesta> =>
    apiClient.get<DiferenciaDiariaRespuesta>(`/api/reportes/diferencia-diaria?mes=${mes}`, {
      signal,
    }),

  /** Ruta de la exportación a Excel (CSV) del reporte de un mes. */
  exportarDiferenciaDiaria: (mes: string): string =>
    `/reportes/diferencia-diaria/exportar?mes=${mes}`,

  ficha: (id: number, signal?: AbortSignal): Promise<ProductoFichaRespuesta> =>
    apiClient.get<ProductoFichaRespuesta>(`/api/gestion/productos/${id}`, { signal }),

  crear: (datos: DatosProducto): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>('/api/gestion/productos', { body: datos }),

  actualizar: (
    id: number,
    datos: DatosProducto,
  ): Promise<{ id: number; mensaje: string; creditos_ajustados: number }> =>
    apiClient.put<{ id: number; mensaje: string; creditos_ajustados: number }>(
      `/api/gestion/productos/${id}`,
      { body: datos },
    ),

  categorias: (signal?: AbortSignal): Promise<{ categorias: CategoriaProducto[] }> =>
    apiClient.get<{ categorias: CategoriaProducto[] }>('/api/gestion/productos/categorias', { signal }),

  crearCategoria: (datos: { nombre: string; descripcion?: string }): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>('/api/gestion/productos/categorias', { body: datos }),

  editarCategoria: (
    id: number,
    datos: { nombre: string; descripcion?: string; estado?: number },
  ): Promise<{ mensaje: string }> =>
    apiClient.put<{ mensaje: string }>(`/api/gestion/productos/categorias/${id}`, { body: datos }),

  crearSubcategoria: (datos: {
    categoria_id: number;
    nombre: string;
    descripcion?: string;
  }): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>('/api/gestion/productos/subcategorias', { body: datos }),

  editarSubcategoria: (
    id: number,
    datos: { nombre: string; descripcion?: string; estado?: number; categoria_id?: number },
  ): Promise<{ mensaje: string }> =>
    apiClient.put<{ mensaje: string }>(`/api/gestion/productos/subcategorias/${id}`, { body: datos }),

  programados: (signal?: AbortSignal): Promise<{ programados: PrecioProgramado[]; pendientes: number }> =>
    apiClient.get<{ programados: PrecioProgramado[]; pendientes: number }>(
      '/api/gestion/productos/precios-programados',
      { signal },
    ),

  programarPrecio: (datos: {
    producto_id: number;
    precio_venta_nuevo: number;
    precio_compra_nuevo?: number;
    fecha_programada: string;
    motivo?: string;
  }): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>('/api/gestion/productos/precios-programados', {
      body: datos,
    }),

  cancelarPrecio: (id: number): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>(`/api/gestion/productos/precios-programados/${id}/cancelar`, {
      body: {},
    }),

  cambiarMarket: (id: number, mostrar: boolean): Promise<{ mensaje: string }> =>
    apiClient.post<{ mensaje: string }>(`/api/gestion/productos/${id}/market`, {
      body: { mostrar },
    }),
};

/** Listado de productos con su stock real. */
export function useProductos(filtros: FiltrosProductos) {
  return useQuery({
    queryKey: productosKeys.listado(filtros),
    queryFn: ({ signal }) => productosApi.listado(filtros, signal),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** Últimas mediciones del tanque del producto. */
export function useMedicionesProducto(id: number | null, limite = 12) {
  return useQuery({
    queryKey: productosKeys.mediciones(id, limite),
    queryFn: ({ signal }) => productosApi.mediciones(id as number, limite, signal),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

/** Refresca el catálogo tras cambiar estado o visibilidad. */
function refrescarProductos(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: [CLAVE, 'productos'] });
}

/** Activa o desactiva un producto. */
export function useCambiarEstadoProducto() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, estado }: { id: number; estado: boolean }) =>
      productosApi.cambiarEstado(id, estado),
    onSuccess: () => refrescarProductos(cliente),
  });
}

/** Muestra u oculta un producto en el market. */
export function useCambiarMarketProducto() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, mostrar }: { id: number; mostrar: boolean }) =>
      productosApi.cambiarMarket(id, mostrar),
    onSuccess: () => refrescarProductos(cliente),
  });
}

/** Ficha de un producto para el alta y la edición. */
export function useProductoFicha(id: number | null) {
  return useQuery({
    queryKey: productosKeys.ficha(id),
    queryFn: ({ signal }) => productosApi.ficha(id as number, signal),
    enabled: id !== null,
  });
}

/** Categorías y subcategorías. */
export function useCategorias(activo = true) {
  return useQuery({
    queryKey: productosKeys.categorias(),
    queryFn: ({ signal }) => productosApi.categorias(signal),
    enabled: activo,
    staleTime: 30_000,
  });
}

/** Precios programados. */
export function usePreciosProgramados(activo = true) {
  return useQuery({
    queryKey: productosKeys.programados(),
    queryFn: ({ signal }) => productosApi.programados(signal),
    enabled: activo,
    staleTime: 15_000,
  });
}

/** Crea o actualiza un producto. */
export function useGuardarProducto(id: number | null) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: DatosProducto): Promise<{ id: number; mensaje: string }> =>
      id === null ? productosApi.crear(datos) : productosApi.actualizar(id, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'productos'] });
    },
  });
}

/** Categorías y subcategorías. */
export function useGuardarCategoria() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: { id?: number; nombre: string; descripcion?: string; estado?: number }) =>
      datos.id === undefined
        ? productosApi.crearCategoria(datos)
        : productosApi.editarCategoria(datos.id, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'productos'] });
    },
  });
}

/** Subcategorías. */
export function useGuardarSubcategoria() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: {
      id?: number;
      categoria_id: number;
      nombre: string;
      descripcion?: string;
      estado?: number;
    }) =>
      datos.id === undefined
        ? productosApi.crearSubcategoria(datos)
        : productosApi.editarSubcategoria(datos.id, datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'productos'] });
    },
  });
}

/** Programa un precio. */
export function useProgramarPrecio() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (datos: {
      producto_id: number;
      precio_venta_nuevo: number;
      precio_compra_nuevo?: number;
      fecha_programada: string;
      motivo?: string;
    }) => productosApi.programarPrecio(datos),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'productos'] });
    },
  });
}

/** Cancela un precio programado. */
export function useCancelarPrecio() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => productosApi.cancelarPrecio(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'productos'] });
    },
  });
}

/** Diferencia diaria de combustibles de un mes. */
export function useDiferenciaDiaria(mes: string) {
  return useQuery({
    queryKey: productosKeys.diferenciaDiaria(mes),
    queryFn: ({ signal }) => productosApi.diferenciaDiaria(mes, signal),
    enabled: /^\d{4}-\d{2}$/.test(mes),
    staleTime: 60_000,
  });
}
